// ---------------------------------------------------------------------------
// Section 2: Layout — layout type, direction, gap, alignment, wrap
// ---------------------------------------------------------------------------

import {
  ArrowRight,
  ArrowDown,
  AlignStartVertical,
  AlignCenterVertical,
  AlignEndVertical,
  StretchHorizontal,
  EqualApproximately,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useEditorStore } from '@/features/page-editor/store/editor-store'
import { InspectorSection } from '@/components/inspector'
import {
  InlineTextField,
  InlineSelectField,
  ToggleGroup,
  JUSTIFY_OPTIONS,
  OVERFLOW_OPTIONS,
  POSITION_OPTIONS,
} from '../fields'
import { getMaxChildren, equalDistribute } from '@/features/page-editor/lib/bounded-flex'
import { setWidthPercent } from '@/features/page-editor/lib/class-utils'
import {
  extractGap,
  setGap,
  extractAlignItems,
  setAlignItems,
  extractJustifyContent,
  setJustifyContent,
  extractFlexDirection,
  setFlexDirection,
  hasFlexWrap,
  setFlexWrap,
  extractOverflow,
  setOverflow,
  extractPosition,
  setPosition,
} from '@/features/page-editor/lib/class-utils'
import type { CogoElement } from '@/features/page-editor/store/types'

// Mini layout structure visualization
function LayoutMinimap({ element }: { element: CogoElement }) {
  const layout = element.layout
  const children = element.children ?? []
  if (children.length === 0) return null

  const direction = layout?.direction ?? 'column'
  const isRow = direction === 'row'
  const isGrid = layout?.type === 'grid'
  const cols = isGrid ? (layout?.columns ?? 2) : undefined
  const maxChildren = getMaxChildren(element.type)
  const count = Math.min(children.length, 20) // Show up to 20 slots, scroll if more

  return (
    <div className="mb-2">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-[10px] text-muted-foreground">
          {children.length}/{maxChildren ?? '\u221E'} children
          {isGrid ? ` \u00B7 ${cols}-col grid` : isRow ? ' \u00B7 row' : ' \u00B7 column'}
        </span>
      </div>
      <div
        className={cn(
          'flex rounded border border-dashed border-muted-foreground/30 bg-muted/30 p-1.5 gap-1',
          isGrid ? 'flex-wrap' : isRow ? 'flex-row' : 'flex-col',
        )}
        style={isGrid ? { maxWidth: '100%' } : isRow ? { maxHeight: 28 } : { maxHeight: 200, overflowY: 'auto' }}
      >
        {children.slice(0, count).map((child) => {
          const isSelected = useEditorStore.getState().selectedIds.includes(child.id)
          const isContainer = child.children && child.children.length > 0
          return (
            <button
              key={child.id}
              type="button"
              className={cn(
                'rounded text-[8px] font-mono leading-none truncate transition-colors cursor-pointer',
                isSelected
                  ? 'bg-blue-500/20 border border-blue-500 text-blue-700'
                  : 'bg-muted border border-transparent hover:border-muted-foreground/30 text-muted-foreground',
              )}
              style={
                isGrid
                  ? { width: `calc(${100 / (cols ?? 2)}% - 4px)`, padding: '3px 4px' }
                  : isRow
                    ? { flex: '1 0 0', minWidth: 0, padding: '3px 4px' }
                    : { width: '100%', padding: '2px 4px' }
              }
              title={`${child.id} (${child.type})`}
              onClick={() => useEditorStore.getState().select(child.id, useEditorStore.getState().selectedBlockName!)}
            >
              {isContainer ? '\u25FB' : ''}{/^el-[0-9a-f]{8}/.test(child.id) ? child.type : child.id}
            </button>
          )
        })}
        {children.length > count && (
          <span className="text-[8px] text-muted-foreground/50 self-center px-1">
            +{children.length - count}
          </span>
        )}
      </div>
    </div>
  )
}

export function LayoutSection({
  element,
  updateProp,
  updateClass,
}: {
  element: CogoElement
  updateProp: (updates: Partial<CogoElement>) => void
  updateClass: (transform: (cls: string) => string) => void
}) {
  const layout = element.layout
  const cls = element.className ?? ''

  return (
    <InspectorSection title="Layout">
      {/* Mini layout structure visualization */}
      <LayoutMinimap element={element} />

      {/* Distribute children equally */}
      {(element.children?.length ?? 0) >= 2 && layout?.type === 'flex' && layout?.direction === 'row' && (
        <button
          type="button"
          className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors px-1 py-0.5 rounded hover:bg-muted"
          onClick={() => {
            const children = element.children ?? []
            const sizes = equalDistribute(children.length)
            children.forEach((child, i) => {
              const updatedClassName = setWidthPercent(child.className ?? '', sizes[i])
              updateProp({}) // trigger re-render
              const store = useEditorStore.getState()
              if (store.selectedBlockName) {
                store.updateElement(store.selectedBlockName, child.id, {
                  className: updatedClassName,
                })
              }
            })
          }}
        >
          <EqualApproximately className="h-3 w-3" />
          Distribute Equally
        </button>
      )}

      {/* Layout type — Framer-style toggle buttons */}
      <div className="flex justify-between items-center gap-2">
        <span className="text-muted-foreground text-xs shrink-0">Type</span>
        <ToggleGroup
          value={layout?.type ?? ''}
          onChange={(type) => {
            if (!type) {
              updateProp({ layout: undefined })
            } else {
              updateProp({
                layout: {
                  ...layout,
                  type: type as 'flex' | 'grid',
                },
              })
            }
          }}
          options={[
            { label: 'Stack', value: 'flex' },
            { label: 'Grid', value: 'grid' },
          ]}
        />
      </div>

      {/* Direction — icon toggle buttons */}
      <div className="flex justify-between items-center gap-2">
        <span className="text-muted-foreground text-xs shrink-0">Direction</span>
        <ToggleGroup
          value={layout?.direction ?? (extractFlexDirection(cls).replace('flex-', '') || '')}
          onChange={(dir) => {
            if (layout) {
              updateProp({
                layout: { ...layout, direction: dir as 'row' | 'column' },
              })
            } else {
              updateClass((c) => setFlexDirection(c, dir ? `flex-${dir}` : ''))
            }
          }}
          options={[
            { label: '\u2192', value: 'row', icon: ArrowRight },
            { label: '\u2193', value: 'column', icon: ArrowDown },
          ]}
        />
      </div>

      {/* Distribute (Justify Content) — Framer-style dropdown kept since it has 6 options */}
      <InlineSelectField
        label="Distribute"
        value={layout?.justify ?? extractJustifyContent(cls)}
        onChange={(justify) => {
          if (layout) {
            updateProp({ layout: { ...layout, justify } })
          } else {
            updateClass((c) => setJustifyContent(c, justify))
          }
        }}
        options={JUSTIFY_OPTIONS}
        show
      />

      {/* Align Items — icon toggle buttons */}
      <div className="flex justify-between items-center gap-2">
        <span className="text-muted-foreground text-xs shrink-0">Align</span>
        <ToggleGroup
          value={layout?.align ?? extractAlignItems(cls)}
          onChange={(align) => {
            if (layout) {
              updateProp({ layout: { ...layout, align } })
            } else {
              updateClass((c) => setAlignItems(c, align))
            }
          }}
          options={[
            { label: 'Start', value: 'start', icon: AlignStartVertical },
            { label: 'Center', value: 'center', icon: AlignCenterVertical },
            { label: 'End', value: 'end', icon: AlignEndVertical },
            { label: 'Stretch', value: 'stretch', icon: StretchHorizontal },
          ]}
        />
      </div>

      {/* Gap */}
      <InlineTextField
        label="Gap"
        value={layout?.gap ?? extractGap(cls)}
        onChange={(gap) => {
          if (layout) {
            updateProp({ layout: { ...layout, gap } })
          } else {
            updateClass((c) => setGap(c, gap))
          }
        }}
        placeholder="e.g. 4, 2, 8"
        show
      />

      {/* Wrap — Framer-style Yes/No toggle */}
      <div className="flex justify-between items-center gap-2">
        <span className="text-muted-foreground text-xs shrink-0">Wrap</span>
        <ToggleGroup
          value={(layout?.wrap ?? hasFlexWrap(cls)) ? 'yes' : 'no'}
          onChange={(v) => {
            const wrap = v === 'yes'
            if (layout) {
              updateProp({ layout: { ...layout, wrap } })
            } else {
              updateClass((c) => setFlexWrap(c, wrap))
            }
          }}
          options={[
            { label: 'Yes', value: 'yes' },
            { label: 'No', value: 'no' },
          ]}
        />
      </div>

      {/* Overflow */}
      <InlineSelectField
        label="Overflow"
        value={extractOverflow(cls)}
        onChange={(val) => updateClass((c) => setOverflow(c, val))}
        options={OVERFLOW_OPTIONS}
        show
      />

      {/* Position */}
      <InlineSelectField
        label="Position"
        value={extractPosition(cls)}
        onChange={(val) => updateClass((c) => setPosition(c, val))}
        options={POSITION_OPTIONS}
        show
      />
    </InspectorSection>
  )
}
